// One chat on a page: its launcher and teaser (in a shadow root), the chat frame (an iframe on the chat server),
// and the private channel between them.
import type {
  FrameToLoader,
  InitPayload,
  Json,
  LoaderToFrame,
  PortEnvelope,
  PublicBotConfig,
} from '@wireface/protocol';
import { docsUrl, ERROR_HELP, isEnvelope, isFrameHello } from '@wireface/protocol';
import { adoptStyles, el, ICONS, onColor, svg, vars } from './dom.ts';
import { Store } from './storage.ts';
import { LOADER_CSS } from './styles.ts';
import type {
  BootOptions,
  ClientTool,
  EventName,
  Json as JsonT,
  ThemeMode,
  VisitorIdentity,
  WirefaceChatEvents,
  WirefaceChatInstance,
  WirefaceError,
} from './types.ts';

interface ConfigResponse {
  config: PublicBotConfig;
  host: string;
  frameUrl: string;
  coreBase: string;
  preview: boolean;
}

type Listener = (data: any) => void;

const TAB_THICKNESS = 46;
const GAP = 14;
const isMobile = () => window.matchMedia('(max-width: 640px), (pointer: coarse) and (max-height: 520px)').matches;
const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export interface InstanceEnv {
  others(): ChatInstance[];
  nonce: string | null;
  removed(i: ChatInstance): void;
}

export class ChatInstance implements WirefaceChatInstance {
  readonly bot: string;
  readonly host: string;
  readonly ready: Promise<void>;
  private opts: BootOptions;
  private meta: ConfigResponse | null = null;
  private hostEl: HTMLElement;
  private shadow: ShadowRoot;
  private root: HTMLDivElement;
  private launcher: HTMLButtonElement | null = null;
  private badge: HTMLSpanElement | null = null;
  private teaser: HTMLDivElement | null = null;
  private frameWrap: HTMLDivElement;
  private scrim: HTMLDivElement;
  private iframe: HTMLIFrameElement | null = null;
  private port: MessagePort | null = null;
  private queue: PortEnvelope[] = [];
  private rpcs = new Map<number, { resolve: (v: unknown) => void; reject: (e: unknown) => void }>();
  private opened = false;
  private visible = true;
  private store: Store;
  private listeners = new Map<string, Set<Listener>>();
  private tools = new Map<string, ClientTool>();
  private conversationId: string | null = null;
  private visitorId: string | null = null;
  private unread = 0;
  private readonly instanceId = Math.random().toString(36).slice(2, 10);
  private cleanups: Array<() => void> = [];
  private scrollLock: { overflow: string } | null = null;
  private destroyed = false;
  private teaserTimer: number | null = null;

  constructor(
    opts: BootOptions,
    host: string,
    private readonly env: InstanceEnv,
  ) {
    this.opts = { ...opts };
    this.bot = opts.bot;
    this.host = host.replace(/\/+$/, '');
    this.store = new Store(`wfc:${this.bot}`, opts.storage ?? 'local');
    for (const [name, tool] of Object.entries(opts.tools ?? {})) this.tools.set(name, tool);

    this.hostEl = el('div', { 'data-wireface-chat': this.bot });
    this.shadow = this.hostEl.attachShadow({ mode: 'open' });
    adoptStyles(this.shadow, LOADER_CSS, env.nonce, `${this.host}/widget.css`);
    this.root = el('div', { class: 'root' });
    this.scrim = el('div', { class: 'scrim', 'aria-hidden': 'true' });
    this.frameWrap = el('div', { class: 'frame', role: 'dialog', 'aria-modal': 'false', inert: true });
    this.root.append(this.scrim, this.frameWrap);
    this.shadow.append(this.root);
    this.scrim.addEventListener('click', () => void this.close('frame'));
    this.ready = this.boot();
  }

  // ---- setup ------------------------------------------------------------------------------------------------

  private async boot() {
    try {
      // as the character the visitor last switched to, so the launcher shows their face
      const query = new URLSearchParams();
      if (this.opts.preview) query.set('preview', this.opts.preview);
      const character = this.store.get().character;
      if (character) query.set('character', character);
      const qs = query.toString();
      const url = `${this.host}/v1/widget/bots/${encodeURIComponent(this.bot)}/config${qs ? `?${qs}` : ''}`;
      const res = await fetch(url, { cache: 'no-cache', credentials: 'omit', mode: 'cors' });
      if (!res.ok) {
        const code = res.status === 403 ? 'origin_not_allowed' : res.status === 404 ? 'config_not_found' : 'network';
        this.fail(code, `Couldn't load the chat (${res.status})`, false);
        return;
      }
      this.meta = (await res.json()) as ConfigResponse;
    } catch {
      this.fail('network', "Couldn't reach the chat server", true);
      return;
    }
    if (this.destroyed) return;
    this.mount();
    this.render();
    const saved = this.store.get();
    this.unread = saved.unread;
    this.updateBadge();
    if (this.layout() === 'inline') {
      this.createFrame();
    } else if (this.opts.open || (saved.open && !isMobile())) {
      void this.open(this.opts.open ? 'api' : 'restore');
    } else if (this.opts.preload === 'idle') {
      const idle =
        (window as Window & { requestIdleCallback?: (fn: () => void) => void }).requestIdleCallback ??
        ((fn: () => void) => setTimeout(fn, 1500));
      idle(() => this.createFrame());
    }
    this.scheduleTeaser();
    this.watchViewport();
  }

  private mount() {
    if (this.layout() === 'inline') {
      const target =
        typeof this.opts.target === 'string' ? document.querySelector<HTMLElement>(this.opts.target) : this.opts.target;
      if (target) {
        this.hostEl.setAttribute('data-layout', 'inline');
        target.append(this.hostEl);
        return;
      }
      console.warn(
        `[Wireface Chat] inline layout: target ${String(this.opts.target)} not found; showing a launcher instead`,
      );
      this.opts.layout = 'panel';
    }
    // a transformed <body> breaks position: fixed, so go on <html> then
    const body = document.body;
    const style = body ? getComputedStyle(body) : null;
    const transformed =
      style && (style.transform !== 'none' || style.filter !== 'none' || style.perspective !== 'none');
    (transformed || !body ? document.documentElement : body).append(this.hostEl);
    // a single-page app that replaces <body> takes us with it: put us back
    const observer = new MutationObserver(() => {
      if (!this.destroyed && !this.hostEl.isConnected) (document.body ?? document.documentElement).append(this.hostEl);
    });
    observer.observe(document.documentElement, { childList: true, subtree: false });
    if (document.body) observer.observe(document.body, { childList: true });
    this.cleanups.push(() => observer.disconnect());
  }

  private cfg(): PublicBotConfig {
    return this.meta!.config;
  }

  /** spa: 'manual' means the page calls page() itself */
  get followsRoutes() {
    return this.opts.spa !== 'manual';
  }

  layout() {
    return this.opts.layout ?? this.meta?.config.appearance.layout ?? 'panel';
  }

  private launcherCfg() {
    const base = this.cfg().appearance.launcher;
    const o = this.opts.launcher ?? {};
    return { ...base, ...o, offset: { ...base.offset, ...(o.offset ?? {}) } };
  }

  private accent() {
    return this.opts.accentColor && /^#[0-9a-f]{6}$/i.test(this.opts.accentColor)
      ? this.opts.accentColor
      : this.cfg().appearance.theme.accent;
  }

  /** Draw (or redraw) the launcher and position everything. */
  private render() {
    const cfg = this.cfg();
    const app = cfg.appearance;
    const l = this.launcherCfg();
    const layout = this.layout();
    const accent = this.accent();
    const name = cfg.agent.name;

    this.root.className = `root layout-${layout}${this.opened ? ' open' : ''}${layout === 'inline' ? ' inline' : ''}`;
    if (l.hideOnMobile && isMobile()) this.root.classList.add('hide-mobile');
    vars(this.hostEl, {
      '--wfc-z': String(this.opts.zIndex ?? app.zIndex),
      '--wfc-accent': accent,
      '--wfc-on-accent': onColor(accent),
      '--wfc-size': l.size,
      '--wfc-radius': app.theme.radius,
    });

    this.launcher?.remove();
    this.launcher = null;
    if (layout !== 'inline' && l.type !== 'none') {
      const thumb =
        cfg.face.thumb && l.icon !== 'chat'
          ? el('img', { class: l.icon === 'avatar' ? 'face' : 'face alive', src: cfg.face.thumb, alt: '' })
          : svg(ICONS.chat, { cls: 'chat', size: Math.round(l.size * 0.45) });
      this.badge = el('span', { class: 'badge', hidden: true, 'aria-hidden': 'true' });
      if (l.type === 'bubble') {
        this.launcher = el(
          'button',
          {
            type: 'button',
            class: 'launcher bubble',
            'aria-label': `Chat with ${name}`,
            'aria-expanded': String(this.opened),
            'aria-haspopup': 'dialog',
          },
          thumb,
          svg(ICONS.close, { cls: 'close', size: Math.round(l.size * 0.42) }),
          el('span', { class: 'live', 'aria-hidden': 'true' }),
          this.badge,
        );
      } else {
        this.launcher = el(
          'button',
          {
            type: 'button',
            class: `launcher tab edge-${l.edge} align-${l.align}`,
            'aria-expanded': String(this.opened),
            'aria-haspopup': 'dialog',
          },
          ...(cfg.face.thumb && l.icon !== 'chat' ? [el('img', { class: 'face', src: cfg.face.thumb, alt: '' })] : []),
          el('span', {}, l.label || `Chat with ${name}`),
          this.badge,
        );
      }
      this.launcher.addEventListener('click', () => void this.toggle('launcher'));
      const warm = () => this.createFrame();
      if ((this.opts.preload ?? 'intent') === 'intent') {
        this.launcher.addEventListener('pointerenter', warm, { once: true });
        this.launcher.addEventListener('focus', warm, { once: true });
        this.launcher.addEventListener('touchstart', warm, { once: true, passive: true });
      }
      this.root.prepend(this.launcher);
      this.updateBadge();
    }
    if (!this.visible) this.launcher?.classList.add('hidden');
    this.place();
    this.frameWrap.setAttribute('aria-label', `Chat with ${name}`);
  }

  /** Where the launcher, teaser and frame sit, from the layout and launcher settings. */
  private place() {
    const app = this.cfg().appearance;
    const l = this.launcherCfg();
    const layout = this.layout();
    const x = l.offset.x;
    const y = l.offset.y;
    const px = (n: number) => `${n}px`;
    const side: 'left' | 'right' =
      l.type === 'tab' ? (l.edge === 'left' ? 'left' : 'right') : l.position.endsWith('left') ? 'left' : 'right';
    const top =
      l.type === 'bubble' || l.type === 'none'
        ? l.position.startsWith('top')
        : l.edge !== 'bottom' && l.align === 'start';
    const pos: Record<string, string | null> = {};

    // the launcher
    for (const k of ['top', 'right', 'bottom', 'left']) pos[`--wfc-l-${k}`] = 'auto';
    if (l.type === 'tab') {
      if (l.edge === 'bottom') {
        pos['--wfc-l-bottom'] = '0px';
        if (l.align === 'start') pos['--wfc-l-left'] = px(x);
        else if (l.align === 'end') pos['--wfc-l-right'] = px(x);
        else pos['--wfc-l-left'] = '50%';
      } else {
        pos[`--wfc-l-${l.edge}`] = '0px';
        if (l.align === 'start') pos['--wfc-l-top'] = px(y);
        else if (l.align === 'end') pos['--wfc-l-bottom'] = px(y);
        else pos['--wfc-l-top'] = '50%';
      }
    } else {
      pos[top ? '--wfc-l-top' : '--wfc-l-bottom'] = px(y);
      pos[`--wfc-l-${side}`] = px(x);
    }

    // the frame
    for (const k of ['top', 'right', 'bottom', 'left']) pos[`--wfc-f-${k}`] = 'auto';
    let w = app.panel.width;
    let h = app.panel.height;
    if (layout === 'drawer') {
      w = app.drawer.width;
      const drawerSide = app.drawer.side === 'auto' ? side : app.drawer.side;
      this.root.classList.add(`side-${drawerSide}`);
      this.root.classList.toggle('modal', app.drawer.modal);
    } else if (layout === 'inline') {
      h = app.panel.height;
    } else {
      if (layout === 'floating') {
        w = app.floating.width;
        h = app.floating.height;
      }
      if (l.type === 'tab' && l.edge !== 'bottom') {
        pos[`--wfc-f-${l.edge}`] = px(TAB_THICKNESS + GAP);
        if (l.align === 'start') pos['--wfc-f-top'] = px(y);
        else if (l.align === 'end') pos['--wfc-f-bottom'] = px(y);
        else pos['--wfc-f-top'] = `calc(50% - ${h / 2}px)`;
      } else if (l.type === 'tab') {
        pos['--wfc-f-bottom'] = px(TAB_THICKNESS + GAP);
        pos[`--wfc-f-${l.align === 'start' ? 'left' : 'right'}`] = px(x);
      } else {
        const lift = l.type === 'none' ? 0 : l.size + GAP;
        pos[top ? '--wfc-f-top' : '--wfc-f-bottom'] = px(y + lift);
        pos[`--wfc-f-${side}`] = px(x);
      }
      pos['--wfc-origin'] = `${top ? 'top' : 'bottom'} ${side}`;
    }
    pos['--wfc-w'] = px(w);
    pos['--wfc-h'] = px(h);
    // never taller than the space between its anchor and the far edge of the window
    const anchored = Number.parseFloat(pos['--wfc-f-bottom'] ?? '') || Number.parseFloat(pos['--wfc-f-top'] ?? '') || 0;
    pos['--wfc-maxh'] = layout === 'drawer' || layout === 'inline' ? null : `calc(100dvh - ${anchored + 12}px)`;

    // the teaser sits by the launcher
    for (const k of ['top', 'right', 'bottom', 'left']) pos[`--wfc-t-${k}`] = 'auto';
    if (l.type === 'bubble') {
      pos[top ? '--wfc-t-top' : '--wfc-t-bottom'] = px(y + l.size + 10);
      pos[`--wfc-t-${side}`] = px(x);
    } else {
      pos['--wfc-t-bottom'] = px(y + 8);
      pos[`--wfc-t-${side}`] = px(l.type === 'tab' && l.edge !== 'bottom' ? TAB_THICKNESS + GAP : x);
    }
    vars(this.hostEl, pos);
  }

  private watchViewport() {
    const vv = window.visualViewport;
    const update = () => {
      const fullscreen =
        this.opened && this.layout() !== 'inline' && this.cfg().appearance.mobile.fullscreen && isMobile();
      this.root.classList.toggle('fullscreen', fullscreen);
      if (vv && fullscreen) vars(this.hostEl, { '--wfc-vv-height': vv.height, '--wfc-vv-top': vv.offsetTop });
      this.lockScroll(fullscreen || (this.opened && this.layout() === 'drawer' && this.cfg().appearance.drawer.modal));
      if (this.port) this.post('display', this.display());
    };
    vv?.addEventListener('resize', update);
    vv?.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    this.cleanups.push(() => {
      vv?.removeEventListener('resize', update);
      vv?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    });
    this.viewportUpdate = update;
  }

  private viewportUpdate: () => void = () => {};

  private lockScroll(on: boolean) {
    const html = document.documentElement;
    if (on && !this.scrollLock) {
      this.scrollLock = { overflow: html.style.overflow };
      html.style.overflow = 'hidden';
    } else if (!on && this.scrollLock) {
      html.style.overflow = this.scrollLock.overflow;
      this.scrollLock = null;
    }
  }

  private display() {
    return {
      mobile: isMobile(),
      reducedMotion: reducedMotion(),
      dark: window.matchMedia('(prefers-color-scheme: dark)').matches,
    };
  }

  // ---- the frame ----------------------------------------------------------------------------------------------

  /** The floating face's own areas (frame coordinates); clicks anywhere else in its box reach the page. */
  private hitRects: Array<{ x: number; y: number; w: number; h: number }> = [];
  private passthrough = false;

  private setPassthrough(on: boolean) {
    if (on === this.passthrough) return;
    this.passthrough = on;
    this.root.classList.toggle('passthrough', on);
  }

  /**
   * The page's pointer: the face's eyes follow it, and over a floating face's empty space clicks go to the page.
   * While the pointer is over the frame (and not passed through) the frame sees it itself.
   */
  private watchPointer() {
    let queued: PointerEvent | null = null;
    const flush = () => {
      const e = queued;
      queued = null;
      if (!e || !this.iframe || !this.opened) return;
      const r = this.iframe.getBoundingClientRect();
      const x = e.clientX - r.left;
      const y = e.clientY - r.top;
      if (this.passthrough && this.hitRects.some(h => x >= h.x && x <= h.x + h.w && y >= h.y && y <= h.y + h.h)) {
        this.setPassthrough(false);
      }
      this.post('pointer', { x, y });
    };
    const onMove = (e: PointerEvent) => {
      if (e.pointerType === 'touch') return;
      if (!queued) requestAnimationFrame(flush);
      queued = e;
    };
    const onLeave = () => this.opened && this.post('pointer', null);
    window.addEventListener('pointermove', onMove, { passive: true });
    document.documentElement.addEventListener('mouseleave', onLeave);
    this.cleanups.push(() => {
      window.removeEventListener('pointermove', onMove);
      document.documentElement.removeEventListener('mouseleave', onLeave);
    });
  }

  private createFrame() {
    if (this.iframe || !this.meta || this.destroyed) return;
    this.watchPointer();
    const src = new URL(this.meta.frameUrl);
    src.searchParams.set('i', this.instanceId);
    if (this.opts.preview) src.searchParams.set('preview', this.opts.preview);
    this.iframe = el('iframe', {
      src: src.href,
      title: `Chat with ${this.cfg().agent.name}`,
      allow: 'microphone; camera; autoplay; clipboard-write',
      sandbox:
        'allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads',
      referrerpolicy: 'strict-origin-when-cross-origin',
    });
    const onMessage = (e: MessageEvent) => this.onWindowMessage(e);
    window.addEventListener('message', onMessage);
    this.cleanups.push(() => window.removeEventListener('message', onMessage));
    this.frameWrap.append(this.iframe);
    const timer = window.setTimeout(() => {
      if (!this.port) this.fail('frame_blocked', "The chat frame didn't load", true);
    }, 15000);
    this.cleanups.push(() => clearTimeout(timer));
  }

  private onWindowMessage(e: MessageEvent) {
    if (!this.iframe || e.source !== this.iframe.contentWindow) return;
    if (e.origin !== new URL(this.host).origin || !isFrameHello(e.data) || e.data.i !== this.instanceId) return;
    const channel = new MessageChannel();
    this.port?.close();
    this.port = channel.port1;
    this.port.onmessage = ev => this.onPortMessage(ev.data);
    const saved = this.store.get();
    const init: InitPayload = {
      bot: this.bot,
      instance: this.instanceId,
      visitor: {
        token: saved.visitorToken,
        conversationId: saved.conversationId,
        resumeToken: saved.resumeToken,
        character: saved.character,
      },
      ...(this.opts.user ? { user: toIdentity(this.opts.user) } : {}),
      ...(this.opts.context ? { context: this.opts.context } : {}),
      ...(this.opts.locale ? { locale: this.opts.locale } : {}),
      hostLang: document.documentElement.lang || undefined,
      hostDir: document.documentElement.dir === 'rtl' ? 'rtl' : 'ltr',
      overrides: {
        ...(this.opts.layout ? { layout: this.opts.layout } : {}),
        ...(this.opts.theme ? { theme: this.opts.theme } : {}),
        ...(this.opts.accentColor ? { accentColor: this.opts.accentColor } : {}),
        ...(this.opts.strings ? { strings: this.opts.strings } : {}),
        ...(this.opts.locale ? { locale: this.opts.locale } : {}),
      },
      tools: this.toolDecls(),
      page: { url: location.href, title: document.title, referrer: document.referrer },
      display: this.display(),
      open: this.opened,
      ...(this.opts.preview ? { preview: this.opts.preview } : {}),
      ...(this.opts.debug ? { debug: true } : {}),
      config: this.meta?.config,
      coreBase: this.meta?.coreBase,
    };
    this.iframe.contentWindow!.postMessage(
      { wfc: 1, t: 'init', i: this.instanceId, d: init },
      new URL(this.host).origin,
      [channel.port2],
    );
    const queued = this.queue;
    this.queue = [];
    for (const m of queued) this.port.postMessage(m);
  }

  private post<K extends keyof LoaderToFrame>(t: K, d: LoaderToFrame[K]) {
    const msg: PortEnvelope = { t, d };
    if (this.port) this.port.postMessage(msg);
    else {
      this.queue.push(msg);
      this.createFrame();
    }
  }

  private onPortMessage(raw: unknown) {
    if (!isEnvelope(raw)) return;
    if (raw.re !== undefined) {
      const rpc = this.rpcs.get(raw.re);
      this.rpcs.delete(raw.re);
      if (rpc) raw.ok === false ? rpc.reject(raw.err) : rpc.resolve(raw.d);
      return;
    }
    const msg = raw as PortEnvelope<keyof FrameToLoader>;
    const d = msg.d as any;
    switch (msg.t) {
      case 'ready': {
        this.conversationId = d.conversationId;
        this.visitorId = d.visitorId;
        // another character (switched to in the chat, or the conversation's own): the launcher shows them
        const was = this.meta?.config;
        if (this.meta && was && (was.face.thumb !== d.config?.face.thumb || was.agent.name !== d.config?.agent.name)) {
          this.meta = { ...this.meta, config: d.config };
          this.render();
        }
        this.emit('ready', { conversationId: d.conversationId, visitorId: d.visitorId });
        break;
      }
      case 'persist':
        // (the frame sends the conversation event itself, new or resumed)
        this.store.set({
          visitorToken: d.visitorToken,
          conversationId: d.conversationId,
          resumeToken: d.resumeToken,
          character: d.character ?? null,
        });
        this.conversationId = d.conversationId;
        break;
      case 'request.close':
        void this.close('frame');
        break;
      case 'request.open':
        void this.open('frame');
        break;
      case 'unread':
        this.unread = this.opened ? 0 : d.count;
        this.store.set({ unread: this.unread });
        this.updateBadge();
        this.emit('unread', { count: this.unread });
        break;
      case 'resize':
        if (this.layout() === 'floating' || this.layout() === 'inline')
          vars(this.hostEl, { '--wfc-w': d.w, '--wfc-h': d.h });
        break;
      case 'hit':
        this.hitRects = d.rects;
        break;
      case 'passthrough':
        this.setPassthrough(!!d.on);
        break;
      case 'tool.call':
        void this.runTool(msg.id!, d);
        break;
      case 'tool.cancel':
        this.toolRuns.get(d.callId)?.abort();
        break;
      case 'navigate':
        try {
          const url = new URL(d.url, location.href);
          if (url.origin === location.origin) location.assign(url.href);
        } catch {
          // ignore bad URLs
        }
        break;
      case 'teaser':
        if (!this.opened) this.showTeaser(d.text);
        break;
      case 'event':
        this.emit(d.name as EventName, d.data);
        break;
      case 'error':
        this.emit('error', {
          code: d.code,
          message: d.message,
          recoverable: d.recoverable,
          docs: docsUrl(this.host, d.code),
        });
        break;
    }
  }

  private toolDecls() {
    return [...this.tools.entries()].map(([name, t]) => ({
      name,
      description: t.description,
      parameters: t.parameters,
      ...(t.confirm !== undefined ? { confirm: t.confirm } : {}),
    }));
  }

  /** page tools running now (the server can cancel them) */
  private toolRuns = new Map<string, AbortController>();

  private async runTool(id: number, call: { callId: string; name: string; args: Json }) {
    const reply = (ok: boolean, d: unknown, err?: string) => {
      this.toolRuns.delete(call.callId);
      this.port?.postMessage({
        t: 'tool.result',
        re: id,
        ok,
        d,
        ...(err ? { err: { code: 'tool_failed', message: err } } : {}),
      });
    };
    const tool = this.tools.get(call.name);
    if (!tool) return reply(false, null, `No tool ${call.name} on this page`);
    this.emit('tool', { callId: call.callId, name: call.name, status: 'started' });
    const ctrl = new AbortController();
    this.toolRuns.set(call.callId, ctrl);
    const timeout = tool.timeoutMs ?? 15000;
    try {
      const result = await Promise.race([
        Promise.resolve(
          tool.handler(call.args, { callId: call.callId, conversationId: this.conversationId, signal: ctrl.signal }),
        ),
        new Promise((_, reject) => setTimeout(() => reject(new Error(`timed out after ${timeout} ms`)), timeout)),
      ]);
      let json = JSON.stringify(result ?? null);
      if (json.length > 16384) json = JSON.stringify({ truncated: json.slice(0, 16000) });
      reply(true, JSON.parse(json));
      this.emit('tool', { callId: call.callId, name: call.name, status: 'succeeded' });
    } catch (err) {
      ctrl.abort();
      reply(false, null, (err as Error)?.message ?? String(err));
      this.emit('tool', { callId: call.callId, name: call.name, status: 'failed' });
    }
  }

  private updateBadge() {
    if (!this.badge) return;
    this.badge.hidden = !this.unread || this.opened;
    this.badge.textContent = this.unread > 9 ? '9+' : String(this.unread);
    const name = this.meta?.config.agent.name ?? '';
    this.launcher?.setAttribute(
      'aria-label',
      this.unread && !this.opened ? `Chat with ${name}, ${this.unread} unread` : `Chat with ${name}`,
    );
  }

  // ---- teaser ------------------------------------------------------------------------------------------------

  private scheduleTeaser() {
    const g = this.cfg().greeting;
    if (!g.enabled || this.opened || this.layout() === 'inline' || (!g.mobile && isMobile())) return;
    const saved = this.store.get();
    if (g.frequency === 'visitor' && saved.teaserDismissed) return;
    if (g.frequency === 'session') {
      try {
        if (sessionStorage.getItem(`wfc:${this.bot}:teaser`)) return;
      } catch {
        // no session storage
      }
    }
    if (!pathMatches(location.pathname, g.pages.include, g.pages.exclude)) return;
    this.teaserTimer = window.setTimeout(() => this.showTeaser(g.text), g.delayMs);
  }

  private showTeaser(text: string) {
    if (this.opened || this.teaser || !this.launcher) return;
    const thumb = this.cfg().face.thumb;
    const close = el('button', { type: 'button', class: 'x', 'aria-label': 'Dismiss' }, svg(ICONS.close, { size: 14 }));
    this.teaser = el(
      'div',
      { class: 'teaser', role: 'status' },
      ...(thumb ? [el('img', { src: thumb, alt: '' })] : []),
      el('span', {}, text),
      close,
    );
    this.teaser.addEventListener('click', e => {
      if (close.contains(e.target as Node)) return;
      void this.open('teaser');
    });
    close.addEventListener('click', () => this.dismissTeaser(true));
    this.root.append(this.teaser);
    try {
      sessionStorage.setItem(`wfc:${this.bot}:teaser`, '1');
    } catch {
      // no session storage
    }
  }

  private dismissTeaser(byVisitor: boolean) {
    if (this.teaserTimer) clearTimeout(this.teaserTimer);
    this.teaser?.remove();
    this.teaser = null;
    if (byVisitor) this.store.set({ teaserDismissed: true });
  }

  // ---- errors and events ---------------------------------------------------------------------------------------

  private fail(code: WirefaceError['code'], message: string, recoverable: boolean) {
    const help = ERROR_HELP[code];
    const docs = docsUrl(this.host, code);
    console.warn(`[Wireface Chat] ${message}. ${help ?? ''} ${docs}`.trim());
    this.emit('error', { code, message, recoverable, docs });
  }

  private emit<K extends EventName>(event: K, data: WirefaceChatEvents[K]) {
    if (this.opts.debug) console.debug('[Wireface Chat]', event, data);
    for (const fn of [...(this.listeners.get(event) ?? [])]) {
      try {
        fn(data);
      } catch (err) {
        console.error('[Wireface Chat] listener failed', err);
      }
    }
    try {
      window.dispatchEvent(
        new CustomEvent(`wirefacechat:${event}`, { detail: { bot: this.bot, ...((data as object) ?? {}) } }),
      );
    } catch {
      // old browsers
    }
  }

  // ---- public API --------------------------------------------------------------------------------------------

  async open(source = 'api') {
    await this.ready;
    if (!this.meta || this.destroyed) return;
    if (this.layout() === 'inline') return;
    if (this.opts.exclusive !== false)
      for (const other of this.env.others()) if (other !== this && other.isOpen()) void other.close('api');
    this.createFrame();
    this.dismissTeaser(false);
    this.opened = true;
    this.root.classList.add('open');
    this.frameWrap.removeAttribute('inert');
    this.launcher?.setAttribute('aria-expanded', 'true');
    this.unread = 0;
    this.updateBadge();
    this.store.set({ open: true, unread: 0 });
    this.viewportUpdate();
    this.post('open', {
      source: source as LoaderToFrame['open']['source'],
      fullscreen: this.root.classList.contains('fullscreen'),
    });
    window.setTimeout(() => this.iframe?.focus(), reducedMotion() ? 0 : 200);
    this.emit('open', { source });
  }

  async close(source = 'api') {
    await this.ready;
    if (!this.opened) return;
    this.opened = false;
    this.root.classList.remove('open');
    this.frameWrap.setAttribute('inert', '');
    this.launcher?.setAttribute('aria-expanded', 'false');
    this.store.set({ open: false });
    this.viewportUpdate();
    this.post('close', { source: source as LoaderToFrame['close']['source'] });
    if (source !== 'api') this.launcher?.focus();
    this.emit('close', { source });
  }

  async toggle(source = 'api') {
    return this.opened ? this.close(source) : this.open(source);
  }

  isOpen() {
    return this.opened;
  }

  show() {
    this.visible = true;
    this.launcher?.classList.remove('hidden');
  }

  hide() {
    this.visible = false;
    this.launcher?.classList.add('hidden');
    this.dismissTeaser(false);
  }

  async update(o: Partial<Omit<BootOptions, 'bot' | 'host' | 'target'>>) {
    await this.ready;
    Object.assign(this.opts, o);
    if (o.storage) this.store.setMode(o.storage);
    if (!this.meta) return;
    this.render();
    this.post('update', {
      ...(o.layout ? { layout: o.layout } : {}),
      ...(o.theme ? { theme: o.theme } : {}),
      ...(o.accentColor ? { accentColor: o.accentColor } : {}),
      ...(o.locale ? { locale: o.locale } : {}),
      ...(o.strings ? { strings: o.strings } : {}),
    });
    if (o.user) await this.identify(o.user);
    if (o.context) await this.setContext(o.context);
  }

  async sendMessage(text: string, o: { open?: boolean } = {}) {
    await this.ready;
    if (o.open !== false) await this.open('api');
    this.post('send', { text });
  }

  async prefill(text: string) {
    await this.ready;
    this.post('prefill', { text });
  }

  async startVoice(o: { mode?: 'conversation' | 'ptt'; camera?: boolean } = {}) {
    await this.open('api');
    this.post('voice.start', o);
  }

  async stopVoice() {
    await this.ready;
    this.post('voice.stop', {});
  }

  async identify(user: VisitorIdentity) {
    await this.ready;
    this.opts.user = user;
    if (this.iframe) this.post('identify', toIdentity(user));
  }

  async setContext(context: Record<string, JsonT>, o: { merge?: boolean } = {}) {
    await this.ready;
    this.opts.context = o.merge === false ? context : { ...(this.opts.context ?? {}), ...context };
    if (this.iframe) this.post('context', { context, merge: o.merge !== false });
  }

  page(info: { url?: string; title?: string } = {}) {
    if (!this.iframe) return;
    this.post('page', { url: info.url ?? location.href, title: info.title ?? document.title });
    if (!this.opened) {
      this.dismissTeaser(false);
      this.scheduleTeaser();
    }
  }

  registerTool<A = any, R extends JsonT = JsonT>(name: string, tool: ClientTool<A, R>) {
    if (!/^[a-zA-Z_][a-zA-Z0-9_]{0,63}$/.test(name))
      throw new Error(`[Wireface Chat] bad tool name ${name}: use letters, digits and _`);
    this.tools.set(name, tool as ClientTool);
    if (this.iframe) this.post('tools.set', { tools: this.toolDecls() });
    return () => {
      this.tools.delete(name);
      if (this.iframe) this.post('tools.set', { tools: this.toolDecls() });
    };
  }

  async newConversation() {
    await this.ready;
    this.post('conversation.new', {});
  }

  async setLocale(locale: string) {
    await this.update({ locale });
  }

  async setTheme(theme: ThemeMode) {
    await this.update({ theme });
  }

  consent(granted: boolean) {
    this.store.setMode(granted ? (this.opts.storage === 'none' ? 'local' : (this.opts.storage ?? 'local')) : 'none');
  }

  getState() {
    return { open: this.opened, conversationId: this.conversationId, visitorId: this.visitorId, unread: this.unread };
  }

  on<K extends EventName>(event: K, fn: (data: WirefaceChatEvents[K]) => void) {
    let set = this.listeners.get(event);
    if (!set) this.listeners.set(event, (set = new Set()));
    set.add(fn as Listener);
    return () => this.off(event, fn);
  }

  once<K extends EventName>(event: K, fn: (data: WirefaceChatEvents[K]) => void) {
    const off = this.on(event, data => {
      off();
      fn(data);
    });
    return off;
  }

  off<K extends EventName>(event: K, fn: (data: WirefaceChatEvents[K]) => void) {
    this.listeners.get(event)?.delete(fn as Listener);
  }

  /** Preview (admin): replace the presentation config live. */
  previewConfig(config: PublicBotConfig) {
    if (!this.meta) return;
    this.meta = { ...this.meta, config };
    this.render();
    this.post('preview.config', { config });
  }

  async shutdown(o: { forget?: boolean } = {}) {
    this.destroyed = true;
    this.lockScroll(false);
    this.post('shutdown', {});
    for (const c of this.cleanups) c();
    this.cleanups = [];
    this.port?.close();
    this.hostEl.remove();
    if (o.forget) this.store.forget();
    this.env.removed(this);
  }
}

function toIdentity(u: VisitorIdentity) {
  return {
    userId: u.id,
    ...(u.hash ? { userHash: u.hash } : {}),
    ...(u.name ? { name: u.name } : {}),
    ...(u.email ? { email: u.email } : {}),
    ...(u.attributes ? { traits: u.attributes } : {}),
  };
}

/** Whether a page path passes include/exclude globs (* matches anything). */
export function pathMatches(path: string, include: string[], exclude: string[]): boolean {
  const re = (glob: string) =>
    new RegExp(
      `^${glob
        .trim()
        .replace(/[.+?^${}()|[\]\\]/g, '\\$&')
        .replace(/\*/g, '.*')}$`,
    );
  if (exclude.some(g => re(g).test(path))) return false;
  return !include.length || include.some(g => re(g).test(path));
}
